import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle, WarningCircle } from "@phosphor-icons/react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [state, setState] = useState("checking"); // checking | valid | invalid | done
  const [errorMsg, setErrorMsg] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) { setState("invalid"); setErrorMsg("Lien invalide."); return; }
    api.get(`/auth/reset-password/${encodeURIComponent(token)}`)
      .then(() => setState("valid"))
      .catch((err) => {
        setState("invalid");
        setErrorMsg(err.response?.data?.detail || "Lien invalide ou expiré.");
      });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { toast.error("Mot de passe trop court (6 caractères min)"); return; }
    if (pw !== pw2) { toast.error("Les deux mots de passe ne correspondent pas"); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setState("done");
      setTimeout(() => navigate("/connexion"), 2500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la réinitialisation");
    } finally { setSubmitting(false); }
  };

  return (
    <Layout>
      <div className="max-w-md mx-auto px-4 py-16">
        <div className="flex justify-center mb-8"><Logo size={56} /></div>
        <div className="bg-white rounded-3xl border border-rose-100 p-8 shadow-sm">
          {state === "checking" && <p className="text-stone-500 text-center" data-testid="reset-password-checking">Vérification du lien...</p>}

          {state === "invalid" && (
            <div className="text-center" data-testid="reset-password-invalid">
              <WarningCircle size={56} weight="duotone" className="mx-auto text-rose-500" />
              <h1 className="font-display text-2xl font-black text-stone-800 mt-3">Lien invalide ou expiré</h1>
              <p className="text-stone-600 mt-2">{errorMsg}</p>
              <Link to="/mot-de-passe-oublie" className="inline-block mt-6" data-testid="reset-password-retry-link">
                <Button className="bg-orange-500 hover:bg-orange-600 rounded-full font-bold px-6">Refaire une demande</Button>
              </Link>
            </div>
          )}

          {state === "valid" && (
            <>
              <h1 className="font-display text-2xl font-black text-stone-800 text-center">Nouveau mot de passe</h1>
              <p className="text-stone-600 text-center mt-2 text-sm">Choisissez un nouveau mot de passe pour votre compte.</p>
              <form onSubmit={submit} className="mt-6 space-y-4" data-testid="reset-password-form">
                <div>
                  <Label htmlFor="pw">Nouveau mot de passe</Label>
                  <Input id="pw" type="password" required minLength={6} value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Au moins 6 caractères" className="rounded-2xl mt-1 py-6" data-testid="reset-password-input" />
                </div>
                <div>
                  <Label htmlFor="pw2">Confirmer le mot de passe</Label>
                  <Input id="pw2" type="password" required minLength={6} value={pw2} onChange={(e) => setPw2(e.target.value)} className="rounded-2xl mt-1 py-6" data-testid="reset-password-input-confirm" />
                </div>
                <Button type="submit" disabled={submitting} className="w-full bg-orange-500 hover:bg-orange-600 rounded-full py-6 font-bold shadow-lg shadow-orange-500/30" data-testid="reset-password-submit">
                  {submitting ? "Enregistrement..." : "Réinitialiser mon mot de passe"}
                </Button>
              </form>
            </>
          )}

          {state === "done" && (
            <div className="text-center" data-testid="reset-password-done">
              <CheckCircle size={56} weight="duotone" className="mx-auto text-emerald-500" />
              <h1 className="font-display text-2xl font-black text-stone-800 mt-3">C'est fait !</h1>
              <p className="text-stone-600 mt-2">Votre mot de passe a été réinitialisé. Redirection vers la connexion...</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
